import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import URLProvider from '@/common/services/URLProvider';
import { useAppStore } from '@/common/stores/AppStore';
import {
  ForwardPinnedItemsConfig,
  ForwardPinnedItemsConfigOptions,
} from '@/forwarding/entities/ForwardConfig/ForwardPinnedItemsConfig';
import { PinnedItem } from '@/forwarding/entities/PinnedItem/PinnedItem';
import { PinnedProduct } from '@/forwarding/entities/PinnedItem/PinnedProduct';
import { useForwardPinnedItemsConfig } from '@/forwarding/hooks/useForwardPinnedItemsConfig';
import { usePinnedItemsStore } from '@/forwarding/stores/PinnedItemsStore';
import { formatAddToCollectionTitle, PinnedProductsPair } from '@/forwarding/utils/pinnedProducts';
import {
  BASE_PRODUCT_TYPE_ORDER,
  BaseProductType,
  baseTypeToAppContextProductType,
} from '@/product/constants/productTypes';

export function usePinItemsActions() {
  const { pinnedItems, unpinAll } = usePinnedItemsStore();
  const createForwardConfig = useForwardPinnedItemsConfig();
  const { setProductTypeContext } = useAppStore();
  const navigate = useNavigate();
  const [forwardConfig, setForwardConfig] = useState<Nullable<ForwardPinnedItemsConfig>>(null);

  const productTypeAndProductsPairs = useMemo(() => toPinnedProductTypePairs(pinnedItems), [pinnedItems]);

  const openForward = useCallback(
    (items: PinnedItem[], options?: ForwardPinnedItemsConfigOptions) => {
      setForwardConfig(createForwardConfig(items, options));
    },
    [createForwardConfig],
  );

  const closeForward = useCallback(() => {
    setForwardConfig(null);
  }, []);

  const compareProducts = useCallback(
    (products: PinnedProduct[], productType: BaseProductType) => {
      const appProductType = baseTypeToAppContextProductType(productType);

      setProductTypeContext(appProductType);
      navigate(
        URLProvider.comparisonPage(
          products.map((product) => product.productId),
          appProductType,
        ),
      );
    },
    [navigate, setProductTypeContext],
  );

  const formatCollectionLabel = useCallback(
    (productType: BaseProductType, productsCount: number) => {
      const hasSeveralProductTypes = productTypeAndProductsPairs.length > 1;

      return formatAddToCollectionTitle(hasSeveralProductTypes ? productType : null, productsCount);
    },
    [productTypeAndProductsPairs],
  );

  const addProductsToCollection = useCallback(
    (products: PinnedProduct[]) => {
      openForward(products, {
        title: formatAddToCollectionTitle(null, products.length),
        isCollectionDestinationOnly: true,
        isShareEnabled: false,
      });
    },
    [openForward],
  );

  return {
    pinnedItems,
    unpinAll,
    productTypeAndProductsPairs,
    forwardConfig,
    openForward,
    closeForward,
    compareProducts,
    addProductsToCollection,
    formatCollectionLabel,
  };
}

function toPinnedProductTypePairs(pinnedItems: PinnedItem[]): PinnedProductsPair[] {
  const productTypeMap = pinnedItems.reduce((acc, item) => {
    if (!(item instanceof PinnedProduct)) {
      return acc;
    }

    const products = acc.get(item.productType) ?? [];

    products.push(item);
    acc.set(item.productType, products);

    return acc;
  }, new Map<BaseProductType, PinnedProduct[]>());

  return [...productTypeMap.entries()].sort(
    ([a], [b]) => BASE_PRODUCT_TYPE_ORDER.indexOf(a) - BASE_PRODUCT_TYPE_ORDER.indexOf(b),
  );
}
